/**
 * SummaryTab - Configuration summary with product details
 */
import { useMemo } from 'react';
import type { FaceIDType } from '@infrastructure/three/prestigeService';
import { PartType } from '@infrastructure/three/prestigeService';
import { FRAME_TEXTURES, BLIND_TEXTURES } from '@domain/constants/ralColors';
import type { UnitType } from '@domain/services/dimensionCalculator';
import { getPrestigeExternalDepthCm } from '@domain/services/prestigeDimensionDisplay';

/* ── Part names ── */

const PART_NAMES: Record<number, string> = {
  [PartType.MANUAL_WIND_SCREEN]: 'Manual Wind Screen',
  [PartType.MOTORIZED_WIND_SCREEN]: 'Motorized Wind Screen',
  [PartType.FRAMELESS_GLASS_DOOR]: 'Frameless Glass Door',
  [PartType.SLIMLINE_FRAME_GLASS_DOOR]: 'Sliding Glass Door',
  [PartType.SLIDING_DOOR_FABRIC]: 'Sliding Door Fabric',
  [PartType.HORIZONTAL_SCREEN_PANEL]: 'Horizontal Screen Panel',
  [PartType.VERTICAL_SCREEN_PANEL]: 'Vertical Screen Panel',
  [PartType.DECO_SCREEN]: 'Deco Screen',
  [PartType.TILTING_HORIZONTAL_SCREEN_PANEL]: 'Tilting Horizontal Screen Panel',
  [PartType.WPC_FENCING_SCREEN]: 'WPC Fencing Screen',
  [PartType.VERTICAL_WPC_DECORATION_SLAT]: 'Vertical WPC Decoration Slat',
  [PartType.WPC_WALL]: 'Solid Composite Privacy Wall',
  [PartType.DECO_YUGUWEN]: 'Deco Fishbone Screen',
  [PartType.DECO_A]: 'Deco A Screen',
  [PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL]: 'Sliding Adjustable Shutter Panel',
};

/* ── Types ── */

type SurfacePartItem = {
  faceId: FaceIDType;
  visible: boolean;
  partType: number | null;
  part: { userData: Record<string, unknown> } | null;
};

type SummaryTabProps = {
  widthValue: number;
  depthValue: number;
  heightValue: number;
  roomCount: number;
  hasWall: boolean;
  unit: UnitType;
  ledsVisible: boolean;
  frameTexture: string;
  louverTexture: string;
  surfacePartList: SurfacePartItem[];
};

type SummaryProduct = {
  seq: number;
  name: string;
  dimension: string;
  description: string;
  count: number;
  key: string;
};

/* ── Dimension helpers ── */

const getPrestigeExternalWidth = (w: number): number => {
  if (w < 23) return Math.round((3.091 + 0.151 * (w - 19)) * 1000) / 10;
  if (w < 25) return Math.round((3.7 + 0.151 * (w - 23)) * 1000) / 10;
  if (w < 39) return Math.round((4.006 + 0.151 * (w - 25)) * 1000) / 10;
  if (w === 39) return 603.2;
  return Math.round((6.032 + 0.151 * (w - 38)) * 1000) / 10;
};
const getPrestigeInternalWidth = (w: number) =>
  Math.round((getPrestigeExternalWidth(w) - 13.6) * 10) / 10;

const getPrestigeInternalDepth = (d: number) =>
  Math.round((getPrestigeExternalDepthCm(d) - 13.6) * 10) / 10;

const getPrestigeExternalHeight = (h: number) =>
  Math.round((h * 100 - 3.4) * 10) / 10;
const getPrestigeInternalHeight = (h: number) =>
  Math.round((h * 100 - 23.4) * 10) / 10;

const cmToFeetInches = (cm: number): string => {
  const total = cm / 2.54;
  let ft = Math.floor(total / 12);
  let inc = Math.round(total % 12);
  if (inc === 12) { ft += 1; inc = 0; }
  return inc === 0 ? `${ft}'` : `${ft}'${inc}"`;
};

const formatDim = (cm: number, u: UnitType) =>
  u === 'inches' ? cmToFeetInches(cm) : `${cm}cm`;

const getTextureInfo = (file: string, list: typeof FRAME_TEXTURES) => {
  const found = list.find((t) => t.img === file);
  return {
    name: found?.name || file.replace('.png', '').toUpperCase(),
    hex: found?.hex || '#888',
  };
};

/* ── Accessory detail helpers (unchanged logic) ── */

const isMergeDoor = (pt: number) =>
  pt === PartType.HORIZONTAL_SCREEN_PANEL ||
  pt === PartType.VERTICAL_SCREEN_PANEL ||
  pt === PartType.DECO_A ||
  pt === PartType.DECO_YUGUWEN ||
  pt === PartType.DECO_SCREEN ||
  pt === PartType.TILTING_HORIZONTAL_SCREEN_PANEL ||
  pt === PartType.WPC_FENCING_SCREEN;

const SLIDING = new Set([
  PartType.FRAMELESS_GLASS_DOOR,
  PartType.SLIMLINE_FRAME_GLASS_DOOR,
  PartType.SLIDING_DOOR_FABRIC,
]);
const WALL_LIKE = new Set([
  PartType.WPC_WALL,
  PartType.VERTICAL_WPC_DECORATION_SLAT,
  PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL,
]);

const fmtAccDim = (w: number, h: number, u: UnitType) =>
  u === 'inches'
    ? `${cmToFeetInches(w / 10)} x ${cmToFeetInches(h / 10)}`
    : `${w}mm x ${h}mm`;

const accColor = (t?: string) => {
  if (!t) return '';
  let n = t.split('.')[0];
  if (n === 'curtain_black') n = 'T01';
  else if (n === 'curtain_texture') n = 'T02';
  else if (n === 'tiltingHorizontalGrid' || n === 'tiltingHorizontalGrid2') n = 'Wood Grain';
  else if (n === 'wpc_ym') n = 'Teak';
  else if (n === 'wpc_yuanmu') n = 'Wood';
  else if (n === 'wpc_qh') n = 'Pebble Beach';
  else if (n === 'wpc_sh') n = 'Iron Timber';
  return n;
};

const getPartInfo = (
  part: { userData: Record<string, unknown> },
  faceId: FaceIDType,
  pt: number,
  wv: number,
  dv: number,
  hv: number,
  u: UnitType,
) => {
  const lz = 150, hl = 120;
  let sH = hv * 1000 - hl, sW = 0, slides = 1;

  if (faceId === 'East' || faceId === 'West') {
    if (isMergeDoor(pt)) {
      slides = 3;
      if (dv === 4) sW = 1235;
      else if (Math.abs(dv - 3.6) <= 0.0001) sW = 1133;
      else sW = 930;
    } else {
      sW = dv * 1000 - lz * 2;
      if (SLIDING.has(pt as any)) slides = 4;
    }
  } else {
    if (isMergeDoor(pt)) {
      slides = 3;
      if (wv <= 22 || (wv > 38 && wv <= 44)) sW = 930;
      else if (wv <= 24 || (wv > 44 && wv <= 48)) sW = 1133;
      else if (wv <= 26 || (wv > 48 && wv <= 52)) sW = 1133;
      else if (wv === 27 || wv === 54) sW = 1342;
      else if (wv <= 29 || (wv > 54 && wv <= 58)) { slides = 4; sW = 930; }
      else if (wv === 30 || wv === 60) { slides = 4; sW = 1133; }
      else if (wv <= 34 || (wv > 60 && wv <= 68)) { slides = 5; sW = 930; }
      else if (wv <= 36 || (wv > 68 && wv <= 72)) { slides = 6; sW = 828; }
      else { slides = 6; sW = 930; }
    } else {
      if (wv < 39) sW = (3000 + (wv - 19) * 151 - lz * 2) | 0;
      else if (wv === 39) sW = (((3000 + (38 - 19) * 151) - lz * 3) / 2) | 0;
      else sW = (((3000 + (wv - 19) * 151) - lz * 3) / 2) | 0;

      if (pt === PartType.FRAMELESS_GLASS_DOOR) slides = 4;
      else if (pt === PartType.SLIMLINE_FRAME_GLASS_DOOR || pt === PartType.SLIDING_DOOR_FABRIC)
        slides = (wv >= 32 && wv < 39) || wv >= 64 ? 6 : 4;
    }
  }

  const dimension = fmtAccDim(sW, sH, u);
  let sidesDetail = '';
  if (isMergeDoor(pt)) {
    let cnt = 0;
    const ary: string[] = [];
    const md = (part.userData.mergeDoors as number | undefined) ?? slides;
    const doors = part.userData.doors as boolean[] | undefined;
    for (let i = 0; i < md; i++) {
      if (!doors || doors[i]) { cnt++; ary.push(`No.${i + 1}`); }
    }
    if (slides !== cnt) { slides = cnt; sidesDetail = `(${ary.join(',')})`; }
  }

  let bC = '', pC = '';
  const data = part.userData.data as Record<string, unknown> | undefined;
  const t2 = data?.texture2 as string | undefined;
  const t1 = data?.texture as string | undefined;
  if (t2) bC = `, Border Color: ${accColor(t2)}`;
  if (t1) {
    const lb = accColor(t1);
    pC = SLIDING.has(pt as any) ? `, Border Color: ${lb}` : `, Part Color: ${lb}`;
  }

  let lr = '';
  if (pt === PartType.FRAMELESS_GLASS_DOOR)
    lr = (part.userData.isLeft as boolean) ? ', Left Side' : ', Right Side';

  let desc = '';
  if (isMergeDoor(pt)) desc = `${lr}${bC}${pC}`;
  else if (WALL_LIKE.has(pt as any)) { desc = `${lr}${bC}${pC}`; slides = 1; }
  else { desc = `${slides} Slides${sidesDetail}${lr}${bC}${pC}`; slides = 1; }
  if (desc.startsWith(', ')) desc = desc.substring(2);

  return { dimension, slides, desc };
};

/* ── Styled sub-components ── */

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#999',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const DimRow = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 13,
      padding: '3px 0',
    }}
  >
    <span style={{ color: '#888' }}>{label}</span>
    <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>
  </div>
);

const Sep = () => <div style={{ height: 1, background: '#eee', margin: '4px 0' }} />;

/* ── Main component ── */

export const SummaryTab = ({
  widthValue,
  depthValue,
  heightValue,
  roomCount,
  hasWall,
  unit,
  ledsVisible,
  frameTexture,
  louverTexture,
  surfacePartList,
}: SummaryTabProps) => {
  const eW = getPrestigeExternalWidth(widthValue);
  const eD = getPrestigeExternalDepthCm(depthValue);
  const eH = getPrestigeExternalHeight(heightValue);
  const iW = getPrestigeInternalWidth(widthValue);
  const iD = getPrestigeInternalDepth(depthValue);
  const iH = getPrestigeInternalHeight(heightValue);

  const frameInfo = getTextureInfo(frameTexture, FRAME_TEXTURES);
  const louverInfo = getTextureInfo(louverTexture, BLIND_TEXTURES);
  const frameName = frameInfo.name;
  const louverName = louverInfo.name;

  const products: SummaryProduct[] = useMemo(() => {
    const items: SummaryProduct[] = [];

    const mainDim =
      unit === 'inches'
        ? `${cmToFeetInches(eW)} x ${cmToFeetInches(eD)} x ${cmToFeetInches(eH)}`
        : `${eW}cm x ${eD}cm x ${eH}cm`;
    const mainDesc = `Frame: ${frameName}, Louver: ${louverName}, ${hasWall ? 'Wall Mounted' : 'Free Standing'}${roomCount > 1 ? `, ${roomCount} Rooms` : ''}`;

    items.push({ seq: 1, name: 'PRESTIGE', dimension: mainDim, description: mainDesc, count: 1, key: 'prestige-main' });

    const map = new Map<string, SummaryProduct>();
    surfacePartList.forEach((sp) => {
      if (sp.partType !== null && sp.visible && sp.part) {
        const pName = PART_NAMES[sp.partType] || `Accessory ${sp.partType}`;
        const info = getPartInfo(sp.part, sp.faceId, sp.partType, widthValue, depthValue, heightValue, unit);
        const key = `${pName}-${info.dimension}-${info.desc}`;
        const ex = map.get(key);
        if (ex) ex.count += info.slides;
        else map.set(key, { seq: 0, name: pName.toUpperCase(), dimension: info.dimension, description: info.desc, count: info.slides, key });
      }
    });

    let seq = 2;
    map.forEach((a) => { a.seq = seq++; items.push(a); });
    return items;
  }, [surfacePartList, eW, eD, eH, unit, frameName, louverName, hasWall, roomCount, widthValue, depthValue, heightValue]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── Heading ── */}
      <p style={{ textAlign: 'center', fontSize: 15, color: '#444', margin: 0 }}>
        Your <strong>configuration</strong> summary:
      </p>

      {/* ── Dimensions ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        {/* External */}
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid #e8e8e8',
            background: '#fafafa',
          }}
        >
          <SectionLabel>External</SectionLabel>
          <DimRow label="Width" value={formatDim(eW, unit)} />
          <DimRow label="Depth" value={formatDim(eD, unit)} />
          <DimRow label="Height" value={formatDim(eH, unit)} />
        </div>

        {/* Internal */}
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid #e8e8e8',
            background: '#fafafa',
          }}
        >
          <SectionLabel>Internal</SectionLabel>
          <DimRow label="Width" value={formatDim(iW, unit)} />
          <DimRow label="Depth" value={formatDim(iD, unit)} />
          <DimRow label="Height" value={formatDim(iH, unit)} />
        </div>
      </div>

      {/* ── Colors ── */}
      <div>
        <SectionLabel>Colors</SectionLabel>
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { label: 'Frame', name: frameName, hex: frameInfo.hex },
            { label: 'Louver', name: louverName, hex: louverInfo.hex },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                background: '#fafafa',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: c.hex,
                  border: '1px solid #d9d9d9',
                  flexShrink: 0,
                }}
              />
              <div>
                <div style={{ fontSize: 11, color: '#999' }}>{c.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{c.name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Options pills ── */}
      <div>
        <SectionLabel>Options</SectionLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            {
              label: `LED Kit: ${ledsVisible ? 'On' : 'Off'}`,
              active: ledsVisible,
            },
            {
              label: hasWall ? 'Wall Mounted' : 'Free Standing',
              active: true,
            },
            {
              label: `${roomCount} Room${roomCount > 1 ? 's' : ''}`,
              active: true,
            },
          ].map((pill) => (
            <span
              key={pill.label}
              style={{
                display: 'inline-block',
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 16,
                background: pill.active ? 'rgba(113,144,62,0.1)' : '#f0f0f0',
                color: pill.active ? '#4a6325' : '#888',
                border: pill.active
                  ? '1px solid rgba(113,144,62,0.3)'
                  : '1px solid #e0e0e0',
              }}
            >
              {pill.label}
            </span>
          ))}
        </div>
      </div>

      <Sep />

      {/* ── Product list ── */}
      <div>
        <SectionLabel>Product Details</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {products.map((p) => (
            <div
              key={p.key}
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                border: p.seq === 1 ? '1.5px solid #71903e' : '1px solid #e8e8e8',
                background: p.seq === 1 ? 'rgba(113,144,62,0.04)' : '#fafafa',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>
                  {p.seq}. {p.name}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#71903e',
                    background: 'rgba(113,144,62,0.1)',
                    padding: '2px 10px',
                    borderRadius: 10,
                  }}
                >
                  x{p.count}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>{p.dimension}</div>
              {p.description && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                  {p.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Sep />

      {/* ── Add to Cart ── */}
      <button
        type="button"
        style={{
          width: '100%',
          padding: '14px 0',
          fontSize: 16,
          fontWeight: 600,
          color: '#fff',
          background: '#71903e',
          border: 'none',
          borderRadius: 24,
          cursor: 'pointer',
          transition: 'background 0.2s',
          letterSpacing: 0.3,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#5f7a34';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#71903e';
        }}
      >
        ADD TO CART
      </button>
    </div>
  );
};
